import React from 'react';
import { Panel, PanelHeader, SurfaceButton, MUTED, DANGER, useSoft } from '../surface';
import { inputClass } from './ui';
import { ChecksList, RingCta } from './studioUi';

const NO_SEND_LINE = 'YOU CAN BUILD AND SAVE THIS. SCHEDULING AND SENDING BELONG TO SOMEONE ELSE.';

/**
 * The SEND panel both builders share: the server's Before-Send checklist, the last test,
 * SAVE · TEST · DUPLICATE, and — for whoever holds the send verb — schedule + SEND NOW.
 * `reach` is the page-mode picture ({ count, noAddress, optedOut, cap }); the admin passes
 * `audienceCount` alone. `noun` is USERS (admin) or PEOPLE (page).
 */
export default function SendPanel({
  channel = 'email', problems = [], canSendNow, dirty, saving, isLocked, isScheduled, status,
  testSentAt, scheduledFor, scheduleError, scheduleInput, onScheduleInput,
  audienceCount = 0, reach, budgetLine, perms, isNew,
  onSave, onTest, onSend, onSchedule, onUnschedule, onDuplicate,
  noun = 'PEOPLE', testTarget = 'your inbox', checks = null, onFix, testSentLabel,
}) {
  const soft = useSoft();
  if (soft && checks) {
    return (
      <SoftSendPanel {...{ channel, checks, canSendNow, dirty, saving, isLocked, isScheduled, status, scheduledFor, scheduleError,
        scheduleInput, onScheduleInput, audienceCount, reach, budgetLine, perms, isNew, onSave, onTest, onSend, onSchedule,
        onUnschedule, onDuplicate, noun, onFix, testSentLabel }} />
    );
  }
  const { canEdit, canCreate, canSend } = perms;
  const count = reach?.count ?? audienceCount;
  const who = `${count.toLocaleString()} ${count === 1 ? noun.replace(/S$/, '') : noun}`;
  const overCap = reach && reach.cap && count > reach.cap;
  const showSendBlock = canSend && !isLocked;
  const sms = channel === 'sms';

  return (
    <Panel data-testid="send-panel">
      <PanelHeader label="SEND" right={budgetLine} />
      <div className="flex flex-col" style={{ padding: 16, gap: 10 }}>
        {reach && (
          <div className="font-mono" style={{ fontSize: 11, color: MUTED, lineHeight: 1.6 }} data-testid="reach-line">
            <span className="font-bold text-black">REACH {count.toLocaleString()}</span>
            {reach.noAddress > 0 && <> · {reach.noAddress.toLocaleString()} with no {sms ? 'number' : 'email'}</>}
            {reach.optedOut > 0 && <> · {reach.optedOut.toLocaleString()} opted out</>}
            {overCap && <span style={{ color: DANGER }}> · over the {reach.cap.toLocaleString()} cap — split it by group</span>}
          </div>
        )}

        {scheduleError && <div className="border-2 px-3 py-2 font-mono text-[11px]" style={{ borderColor: DANGER, color: DANGER }}>The scheduled send didn't fire and this went back to draft: {scheduleError}</div>}
        {isScheduled && scheduledFor && <div className="border-2 border-black px-3 py-2 font-mono text-[11px]">Scheduled for {new Date(scheduledFor).toLocaleString()}. Still editable — but every edit needs a fresh test before it will fire.</div>}
        {isLocked && <div className="border-2 border-black px-3 py-2 font-mono text-[11px]">This {sms ? 'text' : 'email'} is {status === 'Sent' ? 'already sent' : status === 'Failed' ? 'marked failed' : 'sending'} and can no longer be edited. {sms ? 'Texts' : 'Email'} can't be recalled — duplicate it into a new draft to revise and resend.</div>}

        {canEdit && !isLocked && (
          <>
            {problems.length === 0 && !dirty ? (
              <div className="font-mono text-[12px]">Ready to send.</div>
            ) : (
              <ul className="list-none font-mono text-[12px] leading-[1.6]" data-testid="send-problems">
                {dirty && <li>· Unsaved changes — save, then send a test.</li>}
                {problems.map((p, i) => <li key={i}>· {p}</li>)}
              </ul>
            )}
            <div className="font-mono text-[10px]" style={{ color: MUTED }}>{testSentAt ? `last test ${new Date(testSentAt).toLocaleString()}` : 'no test sent'}</div>
          </>
        )}

        {(canEdit || canCreate) && (
          <div className="flex flex-wrap gap-2 pt-1">
            {canEdit && !isLocked && <SurfaceButton variant="fill" size="sm" disabled={saving} onClick={onSave}>{saving ? 'SAVING…' : 'SAVE DRAFT'}</SurfaceButton>}
            {canEdit && !isLocked && <SurfaceButton size="sm" disabled={saving} onClick={onTest} title={`Sends a copy to ${testTarget}`}>SEND TEST TO ME</SurfaceButton>}
            {canCreate && !isNew && <SurfaceButton size="sm" onClick={onDuplicate}>DUPLICATE</SurfaceButton>}
          </div>
        )}

        {showSendBlock ? (
          <div className="flex flex-col gap-2 border-t-2 border-black pt-3">
            <div className="flex flex-wrap items-center gap-2">
              <input type="datetime-local" aria-label="Send at (your time)" className={inputClass} style={{ width: 'auto', padding: '8px 10px', fontSize: 11 }} value={scheduleInput} onChange={(e) => onScheduleInput(e.target.value)} />
              <SurfaceButton size="sm" disabled={!scheduleInput || saving || isNew} onClick={onSchedule}>{isScheduled ? 'RESCHEDULE' : 'SCHEDULE'}</SurfaceButton>
              {isScheduled && <SurfaceButton size="sm" onClick={onUnschedule}>UNSCHEDULE</SurfaceButton>}
            </div>
            <SurfaceButton variant="fill" size="cta" fullWidth disabled={!canSendNow || overCap || isNew} onClick={onSend} data-testid="send-now">
              SEND NOW TO {who}
            </SurfaceButton>
          </div>
        ) : !isLocked && (
          <div className="border-t-2 border-[#E5E7EB] pt-3 font-mono text-[10px] font-bold uppercase tracking-[0.1em]" style={{ color: MUTED }}>{NO_SEND_LINE}</div>
        )}
      </div>
    </Panel>
  );
}

const pill = 'inline-flex items-center justify-center rounded-full font-mono font-extrabold uppercase transition-colors duration-200 focus-visible:shadow-[0_0_0_2px_#fff,0_0_0_4px_#000] focus:outline-none disabled:cursor-not-allowed';

/**
 * 04 SEND in the Email Studio (handoff §4): BEFORE IT GOES — the server's checklist as pill rows
 * (a failed row's FIX → jumps to its step; the test row sends a test), SAVE DRAFT · SEND TEST TO
 * ME · DUPLICATE, the schedule pill, and SEND NOW TO N PEOPLE with the rainbow ring when ready.
 * Drawn only inside a SoftSurface and only when `checks` is passed; the SMS builder keeps the
 * bordered panel above.
 */
function SoftSendPanel({
  checks, canSendNow, dirty, saving, isLocked, isScheduled, status, scheduledFor, scheduleError, scheduleInput, onScheduleInput,
  audienceCount = 0, reach, budgetLine, perms, isNew, onSave, onTest, onSend, onSchedule, onUnschedule, onDuplicate, noun = 'PEOPLE', onFix, testSentLabel,
}) {
  const { canEdit, canCreate, canSend } = perms;
  const count = reach?.count ?? audienceCount;
  const who = `${count.toLocaleString()} ${count === 1 ? noun.replace(/S$/, '') : noun}`;
  const overCap = reach && reach.cap && count > reach.cap;
  const allOk = checks.every((c) => c.ok) && !dirty;
  const ready = allOk && canSendNow && !overCap && !isNew && !isLocked;
  const canSchedule = !!scheduleInput && allOk && !isNew && !saving;
  return (
    <section className="flex flex-col bg-white font-mono" style={{ borderRadius: 28, padding: 24, gap: 18 }} data-testid="send-panel">
      <div className="flex flex-wrap items-baseline justify-between" style={{ gap: 12 }}>
        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.18em' }}>BEFORE IT GOES</span>
        <span style={{ fontSize: 10, letterSpacing: '.12em', color: '#9CA3AF' }}>{budgetLine}</span>
      </div>
      {scheduleError && status === 'Paused' && <div data-testid="paused-reason" style={{ padding: '10px 14px', borderRadius: 14, background: '#FEF3C7', fontSize: 11, lineHeight: 1.6 }}>{scheduleError} Everyone already sent to keeps theirs; the rest weren&rsquo;t emailed. Duplicate it to send again.</div>}
      {scheduleError && status !== 'Paused' && <div style={{ padding: '10px 14px', borderRadius: 14, background: '#FEF2F2', color: '#B91C1C', fontSize: 11, lineHeight: 1.6 }}>The scheduled send didn&rsquo;t fire and this went back to draft: {scheduleError}</div>}
      {isScheduled && scheduledFor && <div style={{ padding: '10px 14px', borderRadius: 14, background: '#ECFEFF', fontSize: 11, lineHeight: 1.6 }}>Scheduled for {new Date(scheduledFor).toLocaleString()}. Still editable, but every edit needs a fresh test before it will fire.</div>}
      {isLocked && status !== 'Paused' && <div style={{ padding: '10px 14px', borderRadius: 14, background: '#F5F5F5', fontSize: 11, lineHeight: 1.6 }}>This email is {status === 'Sent' ? 'sent' : status === 'Failed' ? 'marked failed' : 'sending'} and can no longer be edited. Duplicate it to revise and resend.</div>}
      {overCap && <div style={{ padding: '10px 14px', borderRadius: 14, background: '#FEF2F2', color: '#B91C1C', fontSize: 11 }}>That is over the {reach.cap.toLocaleString()} cap for one email. Split it by group.</div>}

      {!isLocked && <ChecksList checks={checks} onFix={onFix} />}

      {(canEdit || canCreate) && (
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          {canEdit && !isLocked && <button type="button" disabled={saving} onClick={onSave} className={`${pill} bg-[#0A0A0A] text-white hover:opacity-85`} style={{ height: 46, padding: '0 20px', fontSize: 11, letterSpacing: '.14em' }}>{saving ? 'SAVING…' : 'SAVE DRAFT'}</button>}
          {canEdit && !isLocked && <button type="button" disabled={saving} onClick={onTest} className={`${pill} bg-[#F2F2F2] hover:bg-[#E5E5E5]`} style={{ height: 46, padding: '0 20px', fontSize: 11, letterSpacing: '.14em' }}>{testSentLabel || 'SEND TEST TO ME'}</button>}
          {canCreate && !isNew && <button type="button" onClick={onDuplicate} className={`${pill} bg-[#F2F2F2] hover:bg-[#E5E5E5]`} style={{ height: 46, padding: '0 20px', fontSize: 11, letterSpacing: '.14em' }}>DUPLICATE</button>}
        </div>
      )}

      {canSend && !isLocked && (
        <>
          <div className="flex flex-wrap items-center" style={{ gap: 8, paddingTop: 16, borderTop: '1px solid #EDEDED' }}>
            <input type="datetime-local" aria-label="Send at (your time)" value={scheduleInput} onChange={(e) => onScheduleInput(e.target.value)}
              className="rounded-full border-2 border-transparent bg-[#F5F5F5] font-mono focus:border-black focus:outline-none" style={{ height: 46, padding: '0 16px', fontSize: 12 }} />
            <button type="button" disabled={!canSchedule} onClick={onSchedule} className={`${pill} bg-[#F2F2F2]`} style={{ height: 46, padding: '0 20px', fontSize: 11, letterSpacing: '.14em', color: canSchedule ? '#000' : '#9CA3AF' }}>{isScheduled ? 'RESCHEDULE' : 'SCHEDULE'}</button>
            {isScheduled && <button type="button" onClick={onUnschedule} className={`${pill} bg-[#F2F2F2] hover:bg-[#E5E5E5]`} style={{ height: 46, padding: '0 20px', fontSize: 11, letterSpacing: '.14em' }}>UNSCHEDULE</button>}
          </div>
          <RingCta ready={ready} onClick={onSend} testId="send-now">SEND NOW TO {who}</RingCta>
        </>
      )}
      {isLocked && status === 'Sent' && <div className={pill} style={{ height: 60, fontSize: 13, letterSpacing: '.2em', background: '#22C55E', color: '#000' }}>SENT</div>}
      {!canSend && !isLocked && <div style={{ paddingTop: 12, borderTop: '1px solid #EDEDED', fontSize: 10, fontWeight: 700, letterSpacing: '.1em', color: '#6B7280' }}>{NO_SEND_LINE}</div>}
    </section>
  );
}
